<template>
  <div>
    <osv-app-alert />

    <div class="post" v-if="!this.loading">
      <!-- If post is archived -->
      <div v-if="post.archived">
        <b-message type="is-warning">
          {{ $t("postIsArchived") }}
        </b-message>
        <p
          class="text-center mt-5"
          v-if="!inModal && osvConfig.paths.recruitmentPostList"
        >
          <a :href="osvConfig.paths.recruitmentPostList">
            <b-icon class="mr-1" icon="format-list-text" size="is-small" />
            {{ $t("backToJobList") }}
          </a>
        </p>
      </div>

      <!-- If is active -->
      <div v-else>
        <div class="post-image mb-3">
          <img :src="post.image" :alt="post.name" />
        </div>
        <div class="area-post-title">
          <h2 class="post-title is-size-3">{{ post.name }}</h2>
        </div>
        <div class="post-description">
          <div
            class="mb-5 post-content post-catch-phrase"
            v-html="post.catch_phrase"
          />
          <div
            v-if="!post.free_post"
            class="columns is-mobile is-multiline mb-0"
          >
            <div class="column is-half-mobile">
              <dl>
                <dt>
                  <b-icon class="mr-1" icon="calendar-blank" size="is-small" />
                  {{ $t("startDate") }}
                </dt>
                <dd>{{ startMonth() | capitalize }}</dd>
              </dl>
            </div>
            <div
              v-if="post.hours_per_week"
              class="column is-half-mobile"
            >
              <dl>
                <dt>
                  <b-icon
                    class="mr-1"
                    icon="clock-time-eight-outline"
                    size="is-small"
                  />
                  {{ $t("hoursPerWeek") }}
                </dt>
                <dd>
                  {{ post.hours_per_week.toLocaleString() }}
                  {{ $t(`timeUnit.${post.hours_per_week_unit}`) }}
                </dd>
              </dl>
            </div>
            <div class="column is-half-mobile">
              <dl>
                <dt>
                  <b-icon
                    class="mr-1"
                    icon="file-document-edit-outline"
                    size="is-small"
                  />
                  {{ $t("contract") }}
                </dt>
                <dd>{{ post.contract }}</dd>
              </dl>
            </div>
            <div class="column is-half-mobile">
              <dl>
                <dt>
                  <b-icon class="mr-1" icon="cash-multiple" size="is-small" />
                  {{ $t("salary") }}
                </dt>
                <dd>{{ post.salary }}</dd>
              </dl>
            </div>
          </div>

          <!-- Description -->
          <div class="mb-5 post-content">
            <div v-html="post.post_description_content" />
            <div
              v-if="post.content"
              v-html="post.content"
            />
          </div>

          <!-- Experience -->
          <p v-if="!post.free_post && post.experience">
            <strong class="text-grey">
              <b-icon class="mr-1" icon="lightbulb-on-outline" size="is-small" />
              {{ $t("experience") }}
            </strong>
            <span class="text-primary">
              {{ post.experience }}
            </span>
          </p>

          <p
            class="text-center mt-5"
            v-if="!inModal && osvConfig.paths.recruitmentPostList"
          >
            <a :href="osvConfig.paths.recruitmentPostList">
              <b-icon class="mr-1" icon="format-list-text" size="is-small" />
              {{ $t("backToJobList") }}
            </a>
          </p>
        </div>

        <!-- One tenants -->
        <div v-if="post.tenants.length === 1">
          <p class="has-text-weight-bold">
            {{ $t('location') }} :
          </p>
          <p>
            {{ post.tenants[0].name }}
            <span class="text-grey">
              {{ post.tenants[0].zipcode }} {{ post.tenants[0].city }}
            </span>
          </p>
        </div>

        <div class="text-right mt-3">
          <share-btn
            :url="url()"
            :label="$t('share')"
          />
        </div>

        <!-- Candidate -->
        <div>
          <h3 class="apply-title is-size-4">
            {{ $t("candidateTitle") }}
          </h3>
          <recruitment-post-form :post="post" />
        </div>
      </div>
    </div>

    <div v-if="this.loading">
      <spinner />
    </div>
  </div>
</template>

<script>
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { ConfigMixin } from "@/mixins/ConfigMixin";
import { HelpersMixin } from "@/mixins/HelpersMixin";
import cug from "@/services/cug";
import Spinner from "./shared/Spinner";
import VueI18n from "vue-i18n";
import moment from "@/services/momentjs";
import RecruitmentPostForm from "./partial/RecruitmentPostForm";
import OsvAppAlert from "@/components/shared/OsvAppAlert";
import ShareBtn from "@/components/ui/ShareBtn.vue";

export default {
  name: "RecruitmentPost",
  components: { ShareBtn, OsvAppAlert, RecruitmentPostForm, Spinner },
  mixins: [LocaleMixin, HelpersMixin, ConfigMixin],

  props: {
    propsPost: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      post: null,
      loading: true,
      postId: null,
      inModal: false
    };
  },

  created () {
    let urlParams = new URLSearchParams(window.location.search);
    this.PostId = urlParams.get("recruitment_post");

    if (this.propsPost) {
      this.post = this.propsPost;
      this.loading = false;
      this.postId = this.post.id;
      this.inModal = true;
    } else {
      cug
        .recruitmentPost({ id: this.PostId })
        .then(rep => {
          this.post = rep.data;
        })
        .catch(err => {
          this.$root.$emit("OsvAlert_ErrorFromApi", err, "recruitment");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  methods: {
    startMonth () {
      if (this.post.start_date) {
        return moment(this.post.start_date).format("MMMM YYYY");
      } else {
        return this.$t("asSoonAsPossible");
      }
    },

    url () {
      return location.href
    }
  },

  // Translate component
  mounted () {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        startDate: "Job to be filled from",
        asSoonAsPossible: "As soon as possible",
        hoursPerWeek: "Working time",
        contract: "Contract",
        experience: "Experience required :",
        salary: "Salary",
        content: "Description du post",
        candidateTitle: "Apply for a job",
        backToJobList: "Back to jobs list",
        postIsArchived: "This job offer is no longer available",
        share: 'Share this job offer',
        location: 'Location',
        timeUnit: {
          hours_per_week: 'h / week',
          percent: '%'
        }
      },
      fr: {
        startDate: "Prise de poste",
        asSoonAsPossible: "Dès que possible",
        hoursPerWeek: "Temps de travail",
        contract: "Contrat",
        experience: "Expérience exigée :",
        salary: "Salaire",
        content: "Description du poste",
        candidateTitle: "Postuler",
        backToJobList: "Retour à la liste des offres",
        postIsArchived: "Cette offre d'emploi n'est plus disponible",
        share: 'Partager cette annonce',
        location: 'Lieu',
        timeUnit: {
          hours_per_week: 'h / semaine',
          percentage: '%'
        }
      }
    }
  })
};
</script>

<style lang="scss" src="../assets/styles/buefy/base.scss"></style>
<style lang="scss">
@import "../assets/styles/variables.scss";
@import "../assets/styles/helpers.scss";

.post {
  .post-image {
    position: relative;
    width: 100%;
    display: inline-block;
    img {
      height: 100%;
      width: 100%;
      max-height: 400px;
      border-radius: 3px;
      object-fit: cover;
    }
  }
  .area-post-title {
    text-align: center;
    margin-bottom: 1em;
  }
  .post-description {
    .post-catch-phrase {
      font-size: 1.15em;
      font-weight: bold;
      text-align: center;
      color: rgb(120, 120, 120);
    }
    .post-title {
      margin-top: 0;
      margin-bottom: 0;
    }
    dt {
      font-weight: bold;
      text-align: center;
      color: $grey;
    }
    dd {
      text-align: center;
      margin-bottom: 1em;
      padding-top: 0.1em;
      color: $primary;
      font-weight: bold;
    }
    .post-content {
      hyphens: auto;
      text-align: justify;
      p {
        margin-bottom: 1em;
      }
      ul {
        padding-left: 1em;
        li {
          list-style: disc;
        }
      }
    }
  }
  .apply-title {
    margin-top: 3em;
    margin-bottom: 1em;
    color: $primary;
  }
}
</style>
